<template>
  <v-dialog v-model="dialog" max-width="600px" persistent @click:outside="handleClickOutside">
    <v-card class="elevation-8">
      <v-card-title class="d-flex align-center justify-space-between pa-6 bg-primary">
        <div class="text-h5 font-weight-bold text-white">
          {{ isEditMode ? 'Editar Grupo' : 'Crear nuevo grupo' }}
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          color="white"
          @click="closeDialog"
          :disabled="loading"
        />
      </v-card-title>

      <v-divider></v-divider>

      <v-form ref="form" v-model="valid" @submit.prevent="saveGroup">
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formData.name"
                label="Nombre del Grupo"
                variant="outlined"
                :rules="nameRules"
                :disabled="loading"
                prepend-inner-icon="mdi-account-group"
                required
                class="mb-3"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.description"
                label="Descripción"
                variant="outlined"
                :rules="descriptionRules"
                :disabled="loading"
                prepend-inner-icon="mdi-text"
                rows="4"
                counter="500"
                class="mb-3"
              />
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="formData.school_grade_id"
                label="Grado Escolar"
                :items="schoolGradeOptions"
                variant="outlined"
                :rules="schoolGradeRules"
                :disabled="loading"
                :loading="schoolGradesLoading"
                prepend-inner-icon="mdi-school"
                item-title="label"
                item-value="value"
                required
                class="mb-3"
                clearable
              />
            </v-col>

            <v-col cols="12">
              <v-switch
                v-model="formData.is_active"
                label="Grupo Activo"
                color="primary"
                :disabled="loading"
                inset
                class="mb-3"
              >
                <template #label>
                  <div class="d-flex align-center">
                    <v-icon :color="formData.is_active ? 'success' : 'error'" class="mr-2">
                      {{ formData.is_active ? 'mdi-check-circle' : 'mdi-cancel' }}
                    </v-icon>
                    <span>{{ formData.is_active ? 'Activo' : 'Inactivo' }}</span>
                  </div>
                </template>
              </v-switch>
            </v-col>

            <v-col cols="12" v-if="isEditMode && group">
              <v-card variant="tonal" color="info" class="pa-4">
                <div class="d-flex align-center">
                  <v-icon color="info" class="mr-3">mdi-information</v-icon>
                  <div>
                    <div class="text-body-2 font-weight-medium">Información del Grupo</div>
                    <div class="text-caption text-medium-emphasis">
                      Creado: {{ formatDate(group.created_at) }}
                    </div>
                    <div class="text-caption text-medium-emphasis" v-if="group.updated_at">
                      Última modificación: {{ formatDate(group.updated_at) }}
                    </div>
                    <div class="text-caption text-medium-emphasis" v-if="group.pin_count">
                      PINs Asociados: {{ group.pin_count }}
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Error Alert -->
          <v-alert
            v-if="error"
            type="error"
            variant="tonal"
            closable
            @click:close="clearError"
            class="mt-4"
          >
            {{ error }}
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn
            variant="outlined"
            color="grey-darken-1"
            @click="closeDialog"
            :disabled="loading"
            class="text-none mr-3"
          >
            Cancelar
          </v-btn>
          <v-btn
            type="submit"
            color="primary"
            :loading="loading"
            :disabled="!valid || loading"
            class="text-none px-8"
          >
            <v-icon start>
              {{ isEditMode ? 'mdi-content-save' : 'mdi-plus' }}
            </v-icon>
            {{ isEditMode ? 'Actualizar Grupo' : 'Crear Grupo' }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

export default {
  name: 'GroupForm',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      error: null,
      formData: {
        name: '',
        description: '',
        school_grade_id: null,
        is_active: true,
      },
      nameRules: [
        (v) => !!v || 'El nombre del grupo es requerido',
        (v) => (v && v.length >= 3) || 'El nombre del grupo debe tener al menos 3 caracteres',
        (v) => (v && v.length <= 100) || 'El nombre del grupo debe tener menos de 100 caracteres',
        (v) => (v && /^[a-zA-Z0-9\s\-_]+$/.test(v)) || 'El nombre del grupo contiene caracteres inválidos',
      ],
      descriptionRules: [
        (v) => !v || (v && v.length <= 500) || 'La descripción debe tener menos de 500 caracteres',
      ],
      schoolGradeRules: [
        (v) => !!v || 'El grado escolar es requerido',
      ],
    }
  },
  computed: {
    ...mapState('groups', ['error']),
    ...mapState('schoolGrades', { schoolGradesLoading: 'loading' }),
    ...mapGetters('schoolGrades', ['activeGrades']),
    isEditMode() {
      return !!this.group
    },
    schoolGradeOptions() {
      return this.activeGrades.map(grade => ({
        label: `${grade.grade}° Grado`,
        value: grade.id
      }))
    },
  },
  watch: {
    modelValue(val) {
      this.dialog = val
      if (val) {
        this.initializeForm()
      }
    },
    dialog(val) {
      this.$emit('update:modelValue', val)
    },
    group: {
      handler() {
        if (this.dialog) {
          this.initializeForm()
        }
      },
      deep: true,
    },
  },
  async mounted() {
    // Fetch school grades when component is mounted
    await this.fetchSchoolGrades({ is_active: true })
  },
  methods: {
    ...mapActions('groups', ['createGroup', 'updateGroup', 'clearError']),
    ...mapActions('schoolGrades', { fetchSchoolGrades: 'fetchGrades' }),

    initializeForm() {
      if (this.isEditMode && this.group) {
        this.formData = {
          name: this.group.name || '',
          description: this.group.description || '',
          school_grade_id: this.group.school_grade_id || null,
          is_active: this.group.is_active !== undefined ? this.group.is_active : true,
        }
      } else {
        this.formData = {
          name: '',
          description: '',
          school_grade_id: null,
          is_active: true,
        }
      }

      // Clear any previous errors
      this.error = null
      this.clearError()

      // Reset form validation
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },

    async saveGroup() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true
      this.error = null

      try {
        let result

        if (this.isEditMode) {
          result = await this.updateGroup({
            id: this.group.id,
            groupData: { ...this.formData },
          })
        } else {
          result = await this.createGroup({ ...this.formData })
        }

        if (result.success) {
          const action = this.isEditMode ? 'actualizado' : 'creado'
          this.$emit('saved', `Grupo ${action} exitosamente`)
          this.closeDialog()
        } else {
          this.error = result.error || `Error al ${this.isEditMode ? 'actualizar' : 'crear'} grupo`
        }
      } catch (error) {
        this.error = error.message || `Error al ${this.isEditMode ? 'actualizar' : 'crear'} grupo`
      } finally {
        this.loading = false
      }
    },

    handleClickOutside() {
      if (!this.loading) {
        this.closeDialog()
      }
    },

    closeDialog() {
      if (this.loading) return

      this.dialog = false
      this.error = null
      this.clearError()

      // Reset form after a brief delay to prevent flash
      setTimeout(() => {
        this.initializeForm()
      }, 300)
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.v-card-title.bg-primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.v-switch :deep(.v-selection-control__wrapper) {
  height: auto;
}

.v-alert {
  border-radius: 8px;
}

.v-btn.text-none {
  text-transform: none;
  letter-spacing: normal;
}

.v-text-field,
.v-textarea {
  margin-bottom: 0;
}

.v-card {
  border-radius: 12px;
}

.v-card-actions {
  background: rgba(0, 0, 0, 0.02);
}
</style>
